/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-globals */
import { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Icon,
  LinearProgress, Paper, Snackbar, SnackbarCloseReason, Typography, alpha, useTheme,
} from '@mui/material';
import { ContasReceberService, IListagemContasReceber } from '../../../shared/services/api/Financeiro/ContasReceber';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/contaBancaria/ContaBancariaService';
import { EmpresaService } from '../../../shared/services/api/cadastros/Empresa';
import { SessaoCaixaService, ISessaoCaixaAtiva } from '../../../shared/services/api/Financeiro/SessaoCaixa';
import { IVFormErrors, VFieldMoney, VForm, VSwitch, VTextFieldData, useVForm, SeletorFormasPagamento, IFormaPagamentoSelecionada, OPERACOES_PAGAMENTO_CARTAO_SEPARADO } from '../../../shared/forms';
import { FullScreenLoading } from '../../../shared/components';
import { IComprovanteBaixaDados } from './ComprovanteBaixa';
import { DialogVisualizarComprovante } from './DialogVisualizarComprovante';
import * as yup from 'yup';

export interface ConfirmationDialogRawProps {
    value: string;
    open: boolean;
    dados?: IListagemContasReceber[];
    vselecionado: number;
    onClose: (value?: string) => void;
    ontotal: (value?: number) => void;
    onDados: (dados?: IListagemContasReceber[]) => void;
}

interface IFormData {
  total: number;
  datacaixa: Date;
  tipocaixa: boolean;
}
// Mesmas contas de recebíveis fixas do backend (ContasRecebiveisFixas.ts) —
// só oferece o Cartão Crédito/Débito separado se as duas existirem no tenant
// atual, senão "bloqueia" e mantém o Cartão genérico de sempre.
const CONTA_RECEBIVEL_CARTAO_CREDITO_IDCONTA = '87895b23-9f1b-4724-9f05-f487f8302665';
const CONTA_RECEBIVEL_CARTAO_DEBITO_IDCONTA = 'e568e5aa-037f-4683-a59b-7a515d6e2e75';

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  total: yup.number().required(),
  datacaixa: yup.date().required(),
  tipocaixa: yup.boolean().required(),
});

const formatarMoeda = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const DialoBaixa: React.FC<ConfirmationDialogRawProps> = (props: ConfirmationDialogRawProps) => {

  const { onClose, value: valueProp, open, ...other } = props;
  const theme = useTheme();
  const [value, setValue] = useState(valueProp);
  const { formRef, save, } = useVForm();
  const [isLoading, setIsLoading] = useState(false);
  const [showFullScreenLoading, setShowFullScreenLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Realizando baixa...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const requestInProgress = useRef(false);
  const [isLoading2, setIsLoading2] = useState(true);
  const [id, setId] = useState('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'  | 'info'  | 'success'>('warning');
  const [formasPagamentoSelecionadas, setFormasPagamentoSelecionadas] = useState<IFormaPagamentoSelecionada[]>([]);
  const [operacoesCartaoSeparado, setOperacoesCartaoSeparado] = useState(false);
  // Conta de destino não é mais escolhida manualmente — vem da sessão de
  // caixa ativa do usuário (mesmo padrão do Faturamento). Dinheiro cai
  // direto nela; Cartão/PIX Dinâmico roteiam pra recebível fixa; PIX
  // Estático/Transferência usam a conta escolhida por forma (ver
  // SeletorFormasPagamento com capturarDetalhesPagamento). PIX Estático já
  // chega com a conta padrão de PIX configurada no PDV pré-preenchida (ver
  // `buscarContaPadraoPix` abaixo, mesmo padrão de Dialogsendnfe.tsx) — o
  // operador só precisa trocar se quiser outra conta.
  const [sessaoAtiva, setSessaoAtiva] = useState<ISessaoCaixaAtiva | null>(null);
  const [carregandoSessao, setCarregandoSessao] = useState(true);
  const [openComprovante, setOpenComprovante] = useState(false);
  const [comprovanteDados, setComprovanteDados] = useState<IComprovanteBaixaDados | null>(null);

  // Valor recebido não é mais digitado — é sempre a soma do que foi
  // adicionado em formas de pagamento, até o teto do valor selecionado
  // (props.vselecionado). Somar menos que o total é uma baixa parcial
  // válida: o restante continua em aberto pra pagar depois.
  const somaFormas = formasPagamentoSelecionadas.reduce((acc, f) => acc + f.valor, 0);
  const restante = Math.max(0, Number((props.vselecionado - somaFormas).toFixed(2)));
  const percentualPago = props.vselecionado > 0 ? Math.min(100, (somaFormas / props.vselecionado) * 100) : 0;
  const parceiroNome = props.dados?.[0]?.parceiros ?? '';

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    requestInProgress.current = false;
  };

  useEffect(() => {
    setIsLoading2(props.dados?.length === 1);
    if (props.dados && typeof props.dados === 'object') {
      formRef.current?.setFieldValue('total', props.vselecionado);
    }
  }, [id, formRef]);

  const simulateProgress = () => {
    setLoadingProgress(0);
    const interval = setInterval(() => {
      setLoadingProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 400);
    return () => clearInterval(interval);
  };

  const handleSave = (dados: IFormData) => {
    if (requestInProgress.current) return;
    requestInProgress.current = true;
    let progressCleanup: (() => void) | null = null;

    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        if (!sessaoAtiva) {
          requestInProgress.current = false;
          setTms('error');
          setMsg('Abra uma sessão de caixa antes de baixar contas a receber');
          setOpenmsg(true);
          return;
        }
        if (formasPagamentoSelecionadas.length === 0) {
          requestInProgress.current = false;
          setTms('error');
          setMsg('Selecione ao menos uma forma de pagamento');
          setOpenmsg(true);
          return;
        }
        if (somaFormas > props.vselecionado + 0.01) {
          requestInProgress.current = false;
          setTms('error');
          setMsg(`Soma das formas de pagamento (${somaFormas.toFixed(2)}) não pode ultrapassar o valor selecionado (${props.vselecionado.toFixed(2)})`);
          setOpenmsg(true);
          return;
        }
        if (!props.dados || typeof props.dados !== 'object') {
          requestInProgress.current = false;
          return;
        }

        const dadosComFormasPagamento = {
          ...dadosValidados,
          idconta: sessaoAtiva.idconta,
          // Valor recebido é sempre a soma das formas adicionadas — nunca um
          // valor digitado à parte (ver comentário de somaFormas acima).
          recebido: somaFormas,
          formasPagamento: formasPagamentoSelecionadas.map(f => ({
            idoperacao: f.idoperacao,
            valor: f.valor,
            categoria: f.nome,
            idcontadestino: f.idcontadestino,
            datacompensacao: f.datacompensacao,
          })),
        };

        setIsLoading(true);
        setShowFullScreenLoading(true);
        setLoadingMessage('Validando dados...');
        progressCleanup = simulateProgress();
        setLoadingMessage('Processando baixa...');
        setLoadingProgress(40);

        ContasReceberService
          .Baixa(dadosComFormasPagamento, props.dados)
          .then(async (result) => {
            setLoadingProgress(70);

            if (result instanceof Error) {
              setLoadingProgress(100);
              setTimeout(() => {
                setIsLoading(false);
                setShowFullScreenLoading(false);
                requestInProgress.current = false;
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
                if (progressCleanup) progressCleanup();
              }, 300);
              return;
            }

            // Baixa concluída — libera a trava pra próxima baixa poder ser
            // feita. Sem isso, requestInProgress.current ficava travado em
            // true pra sempre (só era resetado nos caminhos de erro acima),
            // desabilitando o botão Finalizar na próxima vez que o dialog
            // abrisse.
            requestInProgress.current = false;

            setLoadingMessage('Gerando comprovante...');
            const [contaResult, empresaResult] = await Promise.all([
              ContaBancariaService.getAllList('', sessaoAtiva.idconta),
              EmpresaService.getById(),
            ]);
            const contaNome = !(contaResult instanceof Error) && contaResult.data[0]
              ? (contaResult.data[0].numeroconta || contaResult.data[0].titular)
              : '';
            const empresa = !(empresaResult instanceof Error) ? empresaResult : undefined;

            setLoadingProgress(100);
            setTimeout(() => {
              setIsLoading(false);
              setShowFullScreenLoading(false);
              if (progressCleanup) progressCleanup();

              setComprovanteDados({
                parceiro: parceiroNome,
                contaNome,
                datalan: dadosValidados.datacaixa,
                contas: (props.dados || []).map((conta) => ({ numero: conta.numero, valor: conta.vreceber, historico: conta.historico })),
                formasPagamento: formasPagamentoSelecionadas.map((f) => ({ nome: f.nome, valor: f.valor })),
                totalBaixado: somaFormas,
                desconto: (props.dados || []).reduce((acc, conta) => acc + Number(conta.desconto || 0), 0),
                acrescimo: (props.dados || []).reduce((acc, conta) => acc + Number(conta.acrescimo || 0), 0),
                logo: props.dados?.[0]?.logo,
                empresaFantasia: empresa?.fantasia,
                empresaRazaoSocial: empresa?.nome,
              });
              setOpenComprovante(true);
            }, 300);
          });
      })
      .catch((errors: yup.ValidationError) => {
        requestInProgress.current = false;
        setIsLoading(false);
        setShowFullScreenLoading(false);
        if (progressCleanup) progressCleanup();
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  const handleCancel = () => {
    if (showFullScreenLoading) return;
    setValue('0');
    onClose('false');
  };

  const handleFecharComprovante = () => {
    setOpenComprovante(false);
    setComprovanteDados(null);
    setValue('0');
    onClose('true');
  };

  useEffect(() => {
    if (!open) {
      setValue(valueProp);
    }else{
      setId(valueProp);
      setFormasPagamentoSelecionadas([]);
      setOpenComprovante(false);
      setComprovanteDados(null);
    }
  }, [valueProp, open]);

  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    Promise.all([
      ContaBancariaService.getAllList('', CONTA_RECEBIVEL_CARTAO_CREDITO_IDCONTA),
      ContaBancariaService.getAllList('', CONTA_RECEBIVEL_CARTAO_DEBITO_IDCONTA),
    ]).then(([credito, debito]) => {
      if (cancelado) return;
      const existemAsDuas = !(credito instanceof Error) && credito.data.length > 0
        && !(debito instanceof Error) && debito.data.length > 0;
      setOperacoesCartaoSeparado(existemAsDuas);
    });
    return () => { cancelado = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setCarregandoSessao(true);
    let cancelado = false;
    SessaoCaixaService.getAtiva().then((resultado) => {
      if (cancelado) return;
      setCarregandoSessao(false);
      setSessaoAtiva(resultado instanceof Error ? null : resultado);
    });
    return () => { cancelado = true; };
  }, [open]);

  // F12 confirma, Esc cancela — Enter só confirma fora de campos de texto
  // (dentro do campo Valor do seletor de formas, Enter já adiciona a forma,
  // ver SeletorFormasPagamento). Mesmo padrão de DialogsendFaturar.tsx.
  useEffect(() => {
    if (!open || openComprovante) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (showFullScreenLoading) return;
      if (event.key === 'F12') {
        event.preventDefault();
        save();
        return;
      }
      if (event.key === 'Escape') {
        handleCancel();
        return;
      }
      const alvo = event.target as HTMLElement | null;
      const digitando = alvo && ['INPUT', 'TEXTAREA'].includes(alvo.tagName);
      if (event.key === 'Enter' && !digitando) {
        event.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openComprovante, showFullScreenLoading]);

  return (
    <>
      {showFullScreenLoading && (
        <FullScreenLoading message={loadingMessage} progress={loadingProgress} />
      )}
      <Dialog
        open={open && !openComprovante}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Icon color="primary">paid</Icon>
          <Typography fontWeight={700} fontSize="1.1rem">
            Baixar contas a receber
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2.5 }}>
          <VForm ref={formRef} onSubmit={handleSave}placeholder={undefined} onPointerEnterCapture={undefined}onPointerLeaveCapture={undefined}>
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 2000} onClose={handleCloseMsg}>
              <Alert
                onClose={handleCloseMsg}
                severity={tms}
                variant="filled"
                sx={{ width: '100%' }}
              >
                {msg}
              </Alert>
            </Snackbar>
            {isLoading && (
              <LinearProgress variant='indeterminate' sx={{ mb: 2 }} />
            )}
            {/* Conta de destino não é mais escolhida aqui — vem da sessão de caixa ativa do
                usuário (Dinheiro cai nela; Cartão/PIX Dinâmico roteiam pra recebível fixa; PIX
                Estático/Transferência usam a conta escolhida por forma, mais abaixo). */}
            {!carregandoSessao && (
              <Alert severity={sessaoAtiva ? 'success' : 'warning'} variant="outlined" sx={{ mb: 2 }}>
                {sessaoAtiva
                  ? `Valores serão recebidos na conta da sessão ativa: ${sessaoAtiva.nomeConta}`
                  : 'Abra uma sessão de caixa antes de baixar contas a receber.'}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={6} sm={6} md={4}>
                <VTextFieldData
                  fullWidth
                  size='small'
                  name='datacaixa'
                  label='Data Recebimento'
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4}>
                <VFieldMoney
                  fullWidth
                  size='small'
                  name='total'
                  label='Total'
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <VSwitch
                  name='tipocaixa'
                  label='Juntar Lançamentos'
                  fdisabled={isLoading2}
                  aliamento='start'
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Formas de pagamento
            </Typography>

            {/* Mesma divisão da dialog de Finalizar Venda do PDV / DialogsendFaturar.tsx:
                lista de formas | valor + pagamentos adicionados | resumo. */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                <SeletorFormasPagamento
                  key={open ? 'aberto' : 'fechado'}
                  total={props.vselecionado}
                  formas={formasPagamentoSelecionadas}
                  onChange={setFormasPagamentoSelecionadas}
                  disabled={isLoading}
                  operacoes={operacoesCartaoSeparado ? OPERACOES_PAGAMENTO_CARTAO_SEPARADO : undefined}
                  capturarDetalhesPagamento
                  habilitarAtalhosTeclado
                  buscarContaPadraoPix
                  capturarDataCompensacaoBoleto
                />
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  width: { xs: '100%', lg: 300 },
                  flexShrink: 0,
                }}
              >
                <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1.5 }}>
                  Resumo da baixa
                </Typography>

                <Typography variant="caption" color="text.secondary">Parceiro</Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5, lineHeight: 1.2 }}>
                  {parceiroNome}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {(props.dados || []).length} conta{(props.dados || []).length === 1 ? '' : 's'} selecionada{(props.dados || []).length === 1 ? '' : 's'}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Total selecionado</Typography>
                  <Typography variant="body1" fontWeight={700}>
                    {formatarMoeda(props.vselecionado)}
                  </Typography>
                </Box>

                {/* Valor recebido não é digitado — é sempre a soma do que foi adicionado
                    em formas de pagamento, logo abaixo. */}
                <Box display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Valor recebido</Typography>
                  <Typography variant="h6" fontWeight={800} color="primary.main">
                    {formatarMoeda(somaFormas)}
                  </Typography>
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={percentualPago}
                  color={percentualPago >= 100 ? 'success' : 'primary'}
                  sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {percentualPago.toFixed(0)}%
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Restante</Typography>
                  <Typography variant="body1" fontWeight={700} color={restante > 0.001 ? 'warning.main' : 'success.main'}>
                    {formatarMoeda(restante)}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </VForm>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant='text' color='inherit' onClick={handleCancel} disabled={showFullScreenLoading}>
            Cancelar
          </Button>
          <Button
            variant='contained'
            startIcon={<Icon>check</Icon>}
            onClick={save}
            disabled={isLoading || requestInProgress.current || !sessaoAtiva}
          >
            {isLoading ? 'Baixando...' : 'Confirmar Baixa (F12)'}
          </Button>
        </DialogActions>
      </Dialog>

      <DialogVisualizarComprovante
        open={openComprovante}
        dados={comprovanteDados}
        onClose={handleFecharComprovante}
      />
    </>
  );
};
